import { useLayoutEffect, useRef } from 'react';

/**
 * FLIP-анимация перестановки строк списка (вкл/выкл теста двигает его наверх/
 * вниз) – без этого React просто перерисовывает список на новом месте без
 * какого-либо движения, переключение выглядит как "прыжок"/подвисание вместо
 * плавного всплытия. Используется и в редактировании (ClientDetailPage), и в
 * мастере создания (ClientCreatePage) — список тестов там локальный (без
 * реального account id), но переключение выглядит так же.
 */
export function useFlipAnimation<K extends string | number>(items: K[]) {
  const nodes = useRef(new Map<K, HTMLElement>());
  // Документ-относительная позиция (top + текущий window.scrollY на момент
  // замера), НЕ голый getBoundingClientRect().top. rect.top — координата
  // относительно вьюпорта: если страница хоть на пиксель проскроллит между
  // предыдущим запуском эффекта и этим, rect.top меняется у ВСЕХ строк одинаково, даже у
  // тех, что реально не переставлялись — и каждая из них получает лишний
  // translateY. Внешне это выглядит как "дёргается весь список", а не только
  // переставленная строка. Прибавляя scrollY на каждом замере, получаем
  // координату в системе документа — она не меняется от самого факта скролла,
  // и разница (delta) отражает ТОЛЬКО реальное перемещение строки в списке.
  const prevTops = useRef(new Map<K, number>());
  // Кэш ref-колбэков по ключу — без этого setItemRef(key) возвращал бы
  // новую функцию на каждый рендер, и React бы отвязывал/привязывал ref
  // (вызывал колбэк с null, потом с элементом) у ВСЕХ строк на каждый
  // рендер панели, а не только при реальном добавлении/удалении строки.
  // Сам DOM-узел от этого не пересоздаётся (key={test.id} стабилен), но
  // это лишняя работа, которой быть не должно.
  const refCallbacks = useRef(new Map<K, (el: HTMLElement | null) => void>());

  // Ключ эффекта — строка из id по порядку, а не сам массив items. items
  // получает НОВУЮ ссылку при любом изменении исходных данных, в том числе
  // когда меняется только active одного теста БЕЗ пересортировки — при
  // зависимости от ссылки эффект (и его синхронный reflow через
  // getBoundingClientRect на ВСЕХ строках) запускался на каждое такое
  // изменение, не только на реальную смену порядка. Строковый ключ равен
  // предыдущему, если порядок id не изменился, — React пропускает эффект по
  // Object.is(prevKey, nextKey), и FLIP не трогает DOM вообще, пока строки
  // правда не переставились.
  const orderKey = items.join(',');

  const measure = () => {
    const scrollY = window.scrollY;
    const tops = new Map<K, number>();
    nodes.current.forEach((el, key) => tops.set(key, el.getBoundingClientRect().top + scrollY));
    return tops;
  };

  useLayoutEffect(() => {
    const nextTops = measure();

    nextTops.forEach((nextTop, key) => {
      const prevTop = prevTops.current.get(key);
      const el = nodes.current.get(key);
      if (prevTop === undefined || !el) return;
      const deltaY = prevTop - nextTop;
      if (!deltaY) return;
      el.style.transition = 'none';
      el.style.transform = `translateY(${deltaY}px)`;
      // Форсируем reflow, чтобы браузер применил transform ДО того, как мы
      // включим transition обратно и уберём его в следующем кадре.
      el.getBoundingClientRect();
      requestAnimationFrame(() => {
        el.style.transition = '';
        el.style.transform = '';
      });
    });

    prevTops.current = nextTops;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ключ по порядку id, см. комментарий у orderKey
  }, [orderKey]);

  const setItemRef = (key: K) => {
    let cb = refCallbacks.current.get(key);
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        if (el) nodes.current.set(key, el);
        else nodes.current.delete(key);
      };
      refCallbacks.current.set(key, cb);
    }
    return cb;
  };

  return {
    setItemRef,
    /**
     * Принудительно обновляет "снимок до" ТЕКУЩИМИ позициями строк, без
     * применения transform — для случаев, когда что-то ДРУГОЕ (не сама
     * перестановка/orderKey) успело визуально сдвинуть строки между двумя
     * изменениями orderKey. Пример: перед удалением строки её высота сначала
     * плавно схлопывается CSS-transition'ом (см. requestRemoveFromSelected в
     * StepTests.tsx/toggleOff в LibraryTestsPanel.tsx) — соседние строки при
     * этом естественным reflow'ом уже подъезжают на новое место, но orderKey
     * (сам список id) при этом ещё не менялся, поэтому эффект выше не
     * перезапускался и не знает об этом сдвиге. Когда чуть позже строка
     * реально удаляется из состояния и orderKey меняется, эффект без этого
     * вызова сравнил бы текущие (уже сдвинутые) позиции со СТАРЫМ снимком "до
     * схлопывания" — получил бы паразитный deltaY, равный тому, что уже и
     * так плавно проехало CSS-анимацией, отбросил бы строку transform'ом
     * обратно на устаревшее место и тут же анимировал бы её назад: "вверх →
     * назад вниз → снова вверх" вместо одного плавного движения. Вызывать
     * непосредственно перед мутацией состояния, вызывающей реальный reorder,
     * когда сторонняя (CSS) анимация уже завершилась.
     */
    resync: () => {
      prevTops.current = measure();
    },
  };
}
